import { supabase } from "@/integrations/supabase/client";
import { db } from "@/lib/billing";

export type AdminAuthUser = {
  id: string;
  email: string | null;
  created_at?: string | null;
  last_sign_in_at?: string | null;
  email_confirmed_at?: string | null;
  user_metadata?: Record<string, unknown>;
};

export type AdminUserProfile = {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  phone_number?: string | null;
  account_status?: string | null;
  created_at: string;
};

export type AdminUserRow = {
  id: string;
  email: string | null;
  full_name: string | null;
  phone_number: string | null;
  company_id: string | null;
  company_name: string | null;
  company_role: string | null;
  company_is_platform_owner: boolean;
  address: string | null;
  country: string | null;
  preferred_language: string | null;
  avatar_url: string | null;
  account_status: string;
  email_verified: boolean;
  roles: string[];
  package_name: string | null;
  package_id: string | null;
  subscription_id: string | null;
  subscription_status: string | null;
  stripe_customer_id: string | null;
  created_at: string | null;
  last_login_at: string | null;
  trial_start: string | null;
  trial_end: string | null;
  renewal_date: string | null;
  websites_count: number;
  articles_count: number;
  media_count: number;
  invoice_count: number;
  payment_status: string | null;
  permissions: string[];
  invitation_status: string | null;
  invitation_id?: string | null;
  invitation_expires_at?: string | null;
  is_invitation_pending?: boolean;
};

export type TeamCompanyOption = {
  id: string;
  name: string;
  slug?: string | null;
  is_platform_owner?: boolean | null;
};

export type ManagedUsersResponse = {
  users: AdminUserRow[];
  companies: TeamCompanyOption[];
  can_manage_all: boolean;
  team_usage?: Array<{ company_id: string; company_name: string; used: number; limit: number | null }>;
};

export const ALL_ADMIN_ROLES = [
  { value: "super_admin", label: "Super Admin" },
  { value: "admin", label: "Admin" },
  { value: "support", label: "Support" },
  { value: "subscriber", label: "User / Subscriber" },
  { value: "editor_in_chief", label: "Editor in Chief" },
  { value: "managing_editor", label: "Managing Editor" },
  { value: "editor", label: "Editor" },
  { value: "reporter", label: "Reporter" },
  { value: "contributor", label: "Contributor" },
  { value: "translator", label: "Translator" },
  { value: "seo_manager", label: "SEO Manager" },
];

export function getInitials(name?: string | null) {
  if (!name) return "?";
  return name.split(" ").map((part) => part[0]).join("").toUpperCase().slice(0, 2);
}

function isForceDeletedUser(authUser?: AdminAuthUser, profile?: any, status?: any) {
  return Boolean(
    authUser?.user_metadata?.force_deleted_at ||
    authUser?.user_metadata?.force_delete_preserved_auth_account ||
    profile?.account_status === "force_deleted" ||
    status?.status === "force_deleted",
  );
}

export async function isCurrentUserAdmin(userId?: string) {
  if (!userId) return false;
  const { data, error } = await supabase.rpc("is_admin" as any, { _user_id: userId });
  if (!error) return Boolean(data);

  const { data: hasAdminRole } = await supabase.rpc("has_role", {
    _user_id: userId,
    _role: "admin",
  });
  if (hasAdminRole) return true;

  const { data: roles } = await db.from("user_roles").select("role").eq("user_id", userId);
  return (roles ?? []).some((role: { role: string }) => role.role === "admin" || role.role === "super_admin");
}

export async function isCurrentUserTeamManager(userId?: string) {
  if (!userId) return false;
  if (await isCurrentUserAdmin(userId)) return true;

  const { data, error } = await (supabase as any)
    .from("company_members")
    .select("role")
    .eq("user_id", userId)
    .in("role", ["owner", "admin"])
    .limit(1);
  if (error) {
    console.warn("Could not check company management role", error);
    return false;
  }
  return Boolean(data?.length);
}

export async function isCurrentUserSuperAdmin(userId?: string) {
  if (!userId) return false;
  const { data, error } = await supabase.rpc("is_super_admin" as any, { _user_id: userId });
  if (!error) return Boolean(data);
  return false;
}

export async function currentUserHasAnyRole(userId: string | undefined, allowedRoles: string[]) {
  if (!userId) return false;
  const { data: roles } = await db.from("user_roles").select("role").eq("user_id", userId);
  return (roles ?? []).some((role: { role: string }) =>
    allowedRoles.includes(role.role) || (role.role === "super_admin" && allowedRoles.includes("admin")),
  );
}

export async function fetchAuthUsers(): Promise<AdminAuthUser[]> {
  const { data, error } = await supabase.functions.invoke("list-team-user-emails", { body: {} });
  if (error) {
    console.warn("Could not load auth user emails; falling back to profiles only", error);
    return [];
  }
  return data?.users ?? [];
}

export async function fetchAdminUsers(): Promise<AdminUserRow[]> {
  const managed = await fetchManagedUsers().catch((error) => {
    console.warn("Could not load managed users from Edge Function; falling back to client queries", error);
    return null;
  });
  if (managed) return managed.users;

  const safeSelect = async (query: PromiseLike<any>, fallback: any[] = []) => {
    const result = await query;
    if (result.error) {
      console.warn("Optional admin user data source unavailable", result.error);
      return fallback;
    }
    return result.data ?? fallback;
  };

  const authUsers = await fetchAuthUsers();

  const profilesResult = await db.from("profiles").select("*");
  if (profilesResult.error) throw profilesResult.error;

  const [roles, statuses, subscriptions, invoices, websites, articles, media, memberships, companies] = await Promise.all([
    safeSelect(db.from("user_roles").select("*")),
    safeSelect(db.from("user_account_status").select("*")),
    safeSelect(db.from("user_subscriptions").select("*, packages(id, name)").order("created_at", { ascending: false })),
    safeSelect(db.from("invoices").select("user_id, status")),
    safeSelect(db.from("websites").select("owner_id")),
    safeSelect(db.from("articles").select("owner_id, author_id")),
    safeSelect(db.from("media_assets").select("owner_id, user_id")),
    safeSelect((supabase as any).from("company_members").select("company_id, user_id, role")),
    safeSelect((supabase as any).from("companies").select("id, name, is_platform_owner")),
  ]);

  const authMap = new Map(authUsers.map((user) => [user.id, user]));
  const profileMap = new Map<string, any>((profilesResult.data ?? []).map((profile: any) => [profile.id, profile]));
  const statusMap = new Map<string, any>(statuses.map((status: any) => [status.user_id, status]));
  const rolesMap = new Map<string, string[]>();
  roles.forEach((role: any) => {
    rolesMap.set(role.user_id, [...(rolesMap.get(role.user_id) ?? []), role.role]);
  });
  const companyMap = new Map<string, any>(companies.map((company: any) => [company.id, company]));
  const membershipMap = new Map<string, any>();
  memberships.forEach((membership: any) => {
    if (!membershipMap.has(membership.user_id)) membershipMap.set(membership.user_id, membership);
  });

  const subscriptionMap = new Map<string, any>();
  subscriptions.forEach((subscription: any) => {
    if (!subscriptionMap.has(subscription.user_id)) subscriptionMap.set(subscription.user_id, subscription);
  });

  const countBy = (rows: any[], keyA: string, keyB?: string) => {
    const map = new Map<string, number>();
    rows.forEach((row) => {
      const id = row[keyA] ?? (keyB ? row[keyB] : null);
      if (id) map.set(id, (map.get(id) ?? 0) + 1);
    });
    return map;
  };
  const websiteCounts = countBy(websites, "owner_id");
  const articleCounts = countBy(articles, "owner_id", "author_id");
  const mediaCounts = countBy(media, "owner_id", "user_id");
  const invoiceCounts = countBy(invoices, "user_id");
  const paymentStatusMap = new Map<string, string>();
  invoices.forEach((invoice: any) => {
    if (invoice.user_id && !paymentStatusMap.has(invoice.user_id)) paymentStatusMap.set(invoice.user_id, invoice.status);
  });

  const allIds = new Set([...authUsers.map((user) => user.id), ...(profilesResult.data ?? []).map((profile: any) => profile.id)]);
  return Array.from(allIds).flatMap((id) => {
    const authUser = authMap.get(id);
    const profile = profileMap.get(id);
    const status = statusMap.get(id);
    if (isForceDeletedUser(authUser, profile, status)) return [];
    const subscription = subscriptionMap.get(id);
    const membership = membershipMap.get(id);
    const company = membership?.company_id ? companyMap.get(membership.company_id) : null;
    return [{
      id,
      email: authUser?.email ?? null,
      full_name: profile?.full_name ?? (authUser?.user_metadata?.full_name as string | undefined) ?? null,
      phone_number: profile?.phone_number ?? status?.phone_number ?? (authUser?.user_metadata?.phone_number as string | undefined) ?? null,
      company_id: membership?.company_id ?? null,
      company_name: company?.name ?? profile?.company_name ?? (authUser?.user_metadata?.company_name as string | undefined) ?? null,
      company_role: membership?.role ?? null,
      company_is_platform_owner: Boolean(company?.is_platform_owner),
      address: profile?.address ?? null,
      country: profile?.country ?? null,
      preferred_language: profile?.preferred_language ?? profile?.language_preference ?? null,
      avatar_url: profile?.avatar_url ?? (authUser?.user_metadata?.avatar_url as string | undefined) ?? null,
      account_status: status?.status ?? profile?.account_status ?? "unknown",
      email_verified: Boolean(status?.email_verified ?? authUser?.email_confirmed_at),
      roles: rolesMap.get(id) ?? [],
      package_name: subscription?.packages?.name ?? null,
      package_id: subscription?.packages?.id ?? subscription?.package_id ?? null,
      subscription_id: subscription?.id ?? null,
      subscription_status: subscription?.status ?? null,
      stripe_customer_id: subscription?.stripe_customer_id ?? null,
      created_at: authUser?.created_at ?? profile?.created_at ?? null,
      last_login_at: status?.last_login_at ?? authUser?.last_sign_in_at ?? null,
      trial_start: subscription?.trial_start ?? status?.trial_started_at ?? null,
      trial_end: subscription?.trial_end ?? status?.trial_ends_at ?? null,
      renewal_date: subscription?.current_period_end ?? null,
      websites_count: websiteCounts.get(id) ?? 0,
      articles_count: articleCounts.get(id) ?? 0,
      media_count: mediaCounts.get(id) ?? 0,
      invoice_count: invoiceCounts.get(id) ?? 0,
      payment_status: paymentStatusMap.get(id) ?? subscription?.status ?? null,
      permissions: [],
      invitation_status: status?.status === "invited" ? "invited" : null,
    }];
  });
}

export async function fetchManagedUsers(): Promise<ManagedUsersResponse> {
  const { data, error } = await supabase.functions.invoke("admin-user-actions", {
    body: { action: "list_users" },
  });
  if (error) throw error;
  return {
    users: data?.users ?? [],
    companies: data?.companies ?? [],
    can_manage_all: Boolean(data?.can_manage_all),
    team_usage: data?.team_usage ?? [],
  };
}

export async function adminUserAction(action: string, payload: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke("admin-user-actions", {
    body: { action, ...payload },
  });
  if (!error) return data;

  const edgeMessage = await extractFunctionError(error);
  if (edgeMessage) throw new Error(edgeMessage);

  console.warn("admin-user-actions Edge Function unavailable; trying limited client fallback", error);
  return adminUserActionFallback(action, payload, error);
}

async function extractFunctionError(error: unknown) {
  const context = (error as { context?: unknown })?.context;
  if (!(context instanceof Response)) return null;

  const status = context.status;
  const body = await context.clone().json().catch(async () => {
    const text = await context.clone().text().catch(() => "");
    return text ? { error: text } : null;
  });
  const message = body?.error ?? body?.message;
  if (message) return `${message}${status ? ` (${status})` : ""}`;
  return status ? `Edge Function returned HTTP ${status}` : null;
}

async function adminUserActionFallback(action: string, payload: Record<string, unknown>, originalError: unknown) {
  const userId = payload.user_id as string | undefined;

  if (action === "update_user" && userId) {
    const { data: currentUser } = await supabase.auth.getUser();
    if (currentUser.user?.id === userId) {
      await supabase.auth.updateUser({
        email: typeof payload.email === "string" && payload.email ? payload.email : undefined,
        data: {
          full_name: payload.full_name,
          phone_number: payload.phone_number,
          avatar_url: payload.avatar_url,
          company_name: payload.company_name,
          preferred_language: payload.preferred_language,
        },
      }).catch((error) => console.warn("Could not update current auth user metadata", error));
    }

    const profilePatch: Record<string, unknown> = {
      full_name: payload.full_name,
      phone_number: payload.phone_number,
      company_name: payload.company_name,
      address: payload.address,
      country: payload.country,
      preferred_language: payload.preferred_language,
      language_preference: payload.preferred_language,
      avatar_url: payload.avatar_url,
      account_status: payload.account_status,
    };
    const profileError = await updateProfileWithExistingColumns(userId, profilePatch);
    if (profileError) {
      throw new Error(
        `Could not reach the admin-user-actions Edge Function, and the direct profile fallback was blocked: ${profileError.message}. Deploy the admin-user-actions function to enable secure admin updates.`,
      );
    }

    const { error: statusError } = await db.from("user_account_status").upsert({
      user_id: userId,
      status: payload.account_status,
      email_verified: Boolean(payload.email_verified),
      phone_number: payload.phone_number,
      updated_at: new Date().toISOString(),
    }, { onConflict: "user_id" });
    if (statusError) {
      console.warn("Optional user_account_status fallback failed", statusError);
    }

    return { ok: true, fallback: true };
  }

  if ((action === "assign_role" || action === "remove_role") && userId && payload.role) {
    const result = action === "assign_role"
      ? await db.from("user_roles").insert({ user_id: userId, role: payload.role })
      : await db.from("user_roles").delete().eq("user_id", userId).eq("role", payload.role);
    if (result.error) {
      throw new Error(
        `Could not reach the admin-user-actions Edge Function, and the direct role fallback was blocked: ${result.error.message}.`,
      );
    }
    return { ok: true, fallback: true };
  }

  if ((action === "suspend_user" || action === "activate_user") && userId) {
    const status = action === "suspend_user" ? "suspended" : "active";
    const profileError = await updateProfileWithExistingColumns(userId, { account_status: status });
    if (profileError) {
      throw new Error(
        `Could not reach the admin-user-actions Edge Function, and the direct status fallback was blocked: ${profileError.message}.`,
      );
    }
    await db.from("user_account_status").update({ status }).eq("user_id", userId);
    return { ok: true, fallback: true };
  }

  throw new Error(
    `Could not reach the admin-user-actions Edge Function. Deploy it before using "${action}". Original error: ${
      originalError instanceof Error ? originalError.message : "Failed to send request"
    }`,
  );
}

async function updateProfileWithExistingColumns(userId: string, patch: Record<string, unknown>) {
  let nextPatch = Object.fromEntries(Object.entries(patch).filter(([, value]) => value !== undefined));

  while (Object.keys(nextPatch).length > 0) {
    const { error } = await db.from("profiles").update(nextPatch).eq("id", userId);
    if (!error) return null;

    const missingColumnMatch = error.message.match(/'([^']+)' column of 'profiles'/);
    const missingColumn = missingColumnMatch?.[1];
    if (!missingColumn || !(missingColumn in nextPatch)) {
      return error;
    }

    console.warn(`profiles.${missingColumn} is not available in this database; retrying fallback without it`);
    const { [missingColumn]: _removed, ...rest } = nextPatch;
    nextPatch = rest;
  }

  return null;
}
